import React, { useState, useEffect } from 'react'
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom'
import { getStudentFeeDetails } from './api/feesApi'
import { appToast } from '../../../components/AppToast'

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN')}`
}

function formatDate(value) {
  if (!value) return '—'
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export default function FeesEntryScreen() {
  const [searchParams] = useSearchParams()
  const params = useParams()
  const navigate = useNavigate()

  const studentId = params.id || searchParams.get('id')

  const [studentDetail, setStudentDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!studentId) {
      setError('No student ID provided.')
      setIsLoading(false)
      return
    }

    loadDetails(studentId)
  }, [studentId])

  const loadDetails = async (id) => {
    try {
      setIsLoading(true)
      setError('')
      const data = await getStudentFeeDetails(id)
      setStudentDetail(data)
    } catch (err) {
      console.error('Failed to load student fee structure:', err)
      setError('Could not load fee schedule for this student.')
      appToast.error('Failed to load student fee schedule')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-4xl text-primary/40 block mb-3">
          progress_activity
        </span>
        <p className="text-sm font-semibold text-on-surface-variant">
          Loading student fee structure...
        </p>
      </div>
    )
  }

  if (error || !studentDetail) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-8 text-center shadow-xs">
          <span className="material-symbols-outlined text-4xl text-error mb-2 block">
            error
          </span>
          <p className="text-sm font-bold text-on-surface">{error || 'Student not found'}</p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admission/fees/list')}
              className="rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary hover:bg-primary/90 transition shadow-2xs cursor-pointer"
            >
              Back to Fees List
            </button>
          </div>
        </div>
      </div>
    )
  }

  const installments = studentDetail.installments || []

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Top Header Canopy ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant mb-1">
            <Link to="/admission/fees/list" className="hover:text-primary transition">
              Fees Management
            </Link>
            <span>/</span>
            <span>Fee Structure &amp; Ledger</span>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {studentDetail.studentName}
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Roll #{studentDetail.rollNo} • {studentDetail.course} • {studentDetail.batch}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/admission/fees/list')}
            className="flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back to Fees List</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`/admission/receipts/entry?studentId=${studentDetail.id}`)}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 transition shadow-2xs cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">receipt_long</span>
            <span>Add Receipt</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`/admission/fees/send-sms?studentId=${studentDetail.id}`)}
            className="flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-high px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-highest transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">sms</span>
            <span>Send SMS</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`/admission/students/entry?id=${studentDetail.id}&mode=edit`)}
            className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-on-primary hover:bg-primary/90 transition shadow-2xs cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">edit</span>
            <span>Edit Admission</span>
          </button>
        </div>
      </section>

      {/* ── Student Profile Header Card ── */}
      <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Institute &amp; Branch</p>
            <p className="mt-1 font-bold text-on-surface text-sm">{studentDetail.company || '—'}</p>
            <p className="text-xs text-on-surface-variant">{studentDetail.branch || '—'}</p>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Course &amp; Batch</p>
            <p className="mt-1 font-bold text-on-surface text-sm">{studentDetail.course || '—'}</p>
            <p className="text-xs text-on-surface-variant">{studentDetail.batch || '—'}</p>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Contact Information</p>
            <p className="mt-1 font-bold text-on-surface text-sm">{studentDetail.mobile || '—'}</p>
            <p className="text-xs text-on-surface-variant">{studentDetail.email || '—'}</p>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Guardian / Scheme</p>
            <p className="mt-1 font-bold text-on-surface text-sm">{studentDetail.guardianName || '—'}</p>
            <p className="text-xs text-on-surface-variant">{studentDetail.scheme || 'Installment Scheme'}</p>
          </div>
        </div>
      </div>

      {/* ── 4 KPI Financial Cards ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">account_balance_wallet</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Fees</p>
            <p className="mt-0.5 text-lg font-extrabold text-on-surface tabular-nums">
              {formatCurrency(studentDetail.totalFees)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">check_circle</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Paid Amount</p>
            <p className="mt-0.5 text-lg font-extrabold text-emerald-700 tabular-nums">
              {formatCurrency(studentDetail.paidAmount)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">pending</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Outstanding Balance</p>
            <p className="mt-0.5 text-lg font-extrabold text-purple-700 tabular-nums">
              {formatCurrency(studentDetail.balance)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">warning</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Overdue Amount</p>
            <p className="mt-0.5 text-lg font-extrabold text-amber-700 tabular-nums">
              {formatCurrency(studentDetail.overdue)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Installment Schedule Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="flex items-center justify-between border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">calendar_month</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Installment Schedule ({installments.length})
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/15 bg-surface-container-low/50 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="px-5 py-3">#</th>
                <th className="px-5 py-3">Particulars / Head</th>
                <th className="px-5 py-3">Due Date</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Paid</th>
                <th className="px-5 py-3 text-right">Balance</th>
                <th className="px-5 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {installments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant">
                    No installment schedule configured for this student.
                  </td>
                </tr>
              ) : (
                installments.map((inst, idx) => (
                  <tr key={inst.id || idx} className="hover:bg-surface-container-low/30 transition">
                    <td className="px-5 py-3 font-mono font-bold text-on-surface-variant">
                      #{inst.installmentNo || idx + 1}
                    </td>
                    <td className="px-5 py-3 font-semibold text-on-surface">
                      {inst.particulars || 'Installment'}
                    </td>
                    <td className="px-5 py-3 text-on-surface-variant font-medium">
                      {formatDate(inst.dueDate)}
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-on-surface tabular-nums">
                      {formatCurrency(inst.amount)}
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-emerald-600 tabular-nums">
                      {formatCurrency(inst.paid)}
                    </td>
                    <td className="px-5 py-3 text-right font-bold text-purple-700 tabular-nums">
                      {formatCurrency(inst.balance)}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold ${
                          inst.status === 'Paid'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-300/60'
                            : inst.status === 'Overdue'
                            ? 'bg-amber-50 text-amber-800 border border-amber-300/60'
                            : inst.status === 'Partial'
                            ? 'bg-blue-50 text-blue-700 border border-blue-300/60'
                            : 'bg-surface-container-high text-on-surface-variant'
                        }`}
                      >
                        {inst.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
